import { computed, onBeforeUnmount, ref, unref, watch } from 'vue';

import { useDisplay } from '@components/composable/display';
import { IN_BROWSER, paramCase, propsFactory } from '@components/utils';

import { useInjectLayout } from '../components/CdsLayout/context';

import type { ILayoutContextProps } from '../components/CdsLayout/context';
import type { PropType, Ref, ComputedRef, CSSProperties } from 'vue';

export interface ILayoutProps {
  modelValue: boolean;
  reveal: boolean;
  revealOffset: number;
  bordered: boolean;
  elevated: boolean;
  height: string | number;
}

export interface IUseLayout {
  layout: ILayoutContextProps;
  size: Ref<number>;
  revealed: Ref<boolean>;
  fixed: Ref<boolean>;
  windowHeight: Ref<number>;
  containerHeight: ComputedRef<number>;
  offset: ComputedRef<number>;
  hidden: ComputedRef<boolean>;
  revealOnFocus: ComputedRef<boolean>;
  classes: ComputedRef<Record<string, boolean>>;
  style: Ref<CSSProperties>;
  updateLocal: (prop: Ref, value: any) => void;
  updateRevealed: () => void;
  onResize: (rect: Pick<DOMRectReadOnly, 'width' | 'height'>) => void;
}

export const makeLayoutProps = propsFactory(
  {
    /**
     * Видимость элемента
     */
    modelValue: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    /**
     * Плавающий элемент
     */
    reveal: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отступ для плавающего элемента
     */
    revealOffset: {
      type: Number as PropType<number>,
      default: 250
    },
    /**
     * Отображение границы
     */
    bordered: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображение тени
     */
    elevated: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Высота элемента
     */
    height: {
      type: [String, Number] as PropType<string | number>,
      default: 50
    }
  },
  'layout'
);

export function useLayout(props: ILayoutProps, componentName: string, view: 'H' | 'F' = 'H'): IUseLayout {
  const isHeader = view === 'H';
  const isFooter = view === 'F';

  const layout = useInjectLayout();
  const display = useDisplay();
  const size = ref(parseInt(props.height as string, 10));
  const revealed = ref(true);

  const fixed = computed(
    () =>
      props.reveal ||
      unref(layout.view).indexOf(view) > -1 ||
      (unref(display.platform).ios && unref(layout.isContainer))
  );

  const windowHeight = ref(!IN_BROWSER || unref(layout.isContainer) ? 0 : window.innerHeight);

  const containerHeight = computed(() =>
    unref(layout.isContainer) ? unref(layout.containerHeight) : unref(windowHeight)
  );

  const offset = computed(() => {
    if (!props.modelValue) return 0;
    if (unref(fixed)) return unref(revealed) ? unref(size) : 0;

    let offset: number = 0;

    if (isHeader) {
      offset = unref(size) - unref(layout.scroll).position.top;
    }

    if (isFooter) {
      offset = unref(layout.scroll).position.top + unref(containerHeight) + unref(size) - unref(layout.height);
    }

    return offset > 0 ? offset : 0;
  });

  const hidden = computed(() => !props.modelValue || (unref(fixed) && !unref(revealed)));

  const revealOnFocus = computed(() => props.modelValue && unref(hidden) && props.reveal);

  const classes = computed(() => ({
    [`${paramCase(componentName)}`]: true,
    [`${paramCase(componentName)}--fixed`]: unref(fixed),
    [`${paramCase(componentName)}--absolute`]: !unref(fixed),
    [`${paramCase(componentName)}--hidden`]: unref(hidden),
    [`${paramCase(componentName)}--bordered`]: props.bordered,
    [`${paramCase(componentName)}--elevated`]: props.elevated,
    [`${paramCase(componentName)}--prevent-focus`]: !props.modelValue
  }));

  const style = computed(() => {
    const view = isHeader ? unref(layout.rows).top : unref(layout.rows).bottom;
    const css: CSSProperties = {};

    if (view[0] === 'l' && layout.left.space) {
      css['left'] = `${layout.left.size}px`;
    }

    if (view[2] === 'r' && layout.right.space) {
      css['right'] = `${layout.right.size}px`;
    }

    return css;
  });

  function updateLayout(prop: string, value: any) {
    layout.update(isHeader ? 'header' : 'footer', prop, value);
  }

  function updateLocal(prop: Ref, value: any) {
    if (unref(prop) !== value) {
      prop.value = value;
    }
  }

  function updateRevealed() {
    if (!props.reveal) return;

    const { direction, position, inflectionPoint } = unref(layout.scroll);

    updateLocal(
      revealed,
      direction === 'up' ||
        position.top - inflectionPoint.top < 100 ||
        unref(layout.height) - unref(containerHeight) - position.top - unref(size) < 300
    );
  }

  function onResize({ height }: Pick<DOMRectReadOnly, 'width' | 'height'>) {
    updateLocal(size, height);
    updateLayout('size', height);
  }

  watch(
    () => props.modelValue,
    (value) => {
      updateLayout('space', value);
      updateLocal(revealed, true);
      layout.animate();
    }
  );

  watch(offset, (value) => {
    updateLayout('offset', value);
  });

  watch(
    () => props.reveal,
    (value) => {
      !value && updateLocal(revealed, props.modelValue);
    }
  );

  const instance = {};

  layout.instances[isHeader ? 'header' : 'footer'] = instance;
  props.modelValue && updateLayout('size', unref(size));
  updateLayout('space', props.modelValue);
  updateLayout('offset', unref(offset));

  onBeforeUnmount(() => {
    if (layout.instances[isHeader ? 'header' : 'footer'] === instance) {
      layout.instances[isHeader ? 'header' : 'footer'] = undefined;
      updateLayout('size', 0);
      updateLayout('offset', 0);
      updateLayout('space', false);
    }
  });

  return {
    layout,
    size,
    revealed,
    fixed,
    windowHeight,
    containerHeight,
    offset,
    hidden,
    revealOnFocus,
    classes,
    style,
    updateLocal,
    updateRevealed,
    onResize
  };
}
